import { X } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';

export interface TagChipInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  addLabel?: string;
  'aria-label'?: string;
}

/**
 * Saisie de tags/codes promo en chips — `02` §10 (conditions.discountCodes,
 * action.tags) : champ texte + bouton « Ajouter », sans dépendance externe.
 */
export function TagChipInput({
  value,
  onChange,
  placeholder = 'Ajouter…',
  addLabel = 'Ajouter',
  'aria-label': ariaLabel,
}: TagChipInputProps): JSX.Element {
  const [draft, setDraft] = useState('');

  function commit(): void {
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      return;
    }
    if (!value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setDraft('');
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Input
          type="text"
          value={draft}
          placeholder={placeholder}
          aria-label={ariaLabel ?? placeholder}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
          }}
        />
        <Button type="button" variant="outline" onClick={commit}>
          {addLabel}
        </Button>
      </div>
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {value.map((tag) => (
            <li key={tag} className="flex">
              <Badge tone="gray" className="h-6 gap-0.5 pl-2 pr-0.5">
                {tag}
                <button
                  type="button"
                  aria-label={`Retirer ${tag}`}
                  className="inline-flex size-5 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => {
                    onChange(value.filter((entry) => entry !== tag));
                  }}
                >
                  <X className="size-3" aria-hidden="true" />
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
